// Game Statistics
export interface GameStats {
  difficulty: DifficultyLevel;
  moves: number;
  time: number | string; // в секундах или строка для отображения
  showDifficulty?: boolean;
  leftX: number;
  rightX: number;
}

export type DifficultyLevel = 'Easy' | 'Medium' | 'Hard';

// UI State
export interface UIState {
  gameStats: GameStats;
  modals: ModalsState;
  settings: Checkboxes[];
  design: DesignState;
  indicators: IndicatorsState;
  buttons: ButtonsState;
  labels: LabelsState;
  isExpanded: boolean;
  tutorialActive: boolean;
  isInitialized: boolean;
}

export interface Checkboxes {
  key: string;
  label: string;
  checked: boolean;
  visible: boolean;
}

// Modals State
export interface ModalsState {
  designModal: {
    isOpen: boolean;
    activeTab: DesignTab;
  };
  settingsModal: {
    isOpen: boolean;
  };
}

export type DesignTab = 'patterns' | 'cards' | 'backgrounds';

// Settings State
export interface SettingsState {
  soundEnabled: boolean;
  animationsEnabled: boolean;
  autoCompleteEnabled: boolean;
  hintsEnabled: boolean;
}

// Design State
export interface DesignState {
  selectedPattern: string;
  selectedCardTheme: string;
  selectedBackground: string;
}

// Indicators State
export interface IndicatorsState {
  shareIndicator: boolean;
  hintsIndicator: boolean;
  hintsCount: number;
  settingsIndicator: boolean;
}

// Buttons State
export interface ButtonsState {
  undoDisabled: boolean;
  hintsDisabled: boolean;
  magicWandDisabled: boolean;
  showMagicWand: boolean;
  showAutocomplete: boolean;
  hideDesignFirstTab: boolean;
  magicWandsCount: number;
}

// Labels State  
export interface LabelsState {
  movesLabel: string;
  timeLabel: string;
  settingsText: string;
  feedbackText: string;
  designText: string;
  autocompleteText: string;
}

// Action Types
export enum UIActionTypes {
  // Universal action for updating any part of UI state
  UPDATE_UI_STATE = 'UPDATE_UI_STATE',
  // Game Stats
  UPDATE_GAME_STATS = 'UPDATE_GAME_STATS',
  SET_DIFFICULTY = 'SET_DIFFICULTY',
  INCREMENT_MOVES = 'INCREMENT_MOVES',
  UPDATE_TIME = 'UPDATE_TIME',
  RESET_GAME_STATS = 'RESET_GAME_STATS',
  
  // Modals
  OPEN_DESIGN_MODAL = 'OPEN_DESIGN_MODAL',
  CLOSE_DESIGN_MODAL = 'CLOSE_DESIGN_MODAL',
  SET_DESIGN_TAB = 'SET_DESIGN_TAB',
  OPEN_SETTINGS_MODAL = 'OPEN_SETTINGS_MODAL',
  CLOSE_SETTINGS_MODAL = 'CLOSE_SETTINGS_MODAL',
  CLOSE_ALL_PANELS = 'CLOSE_ALL_PANELS',
  
  // Settings
  TOGGLE_SETTING = 'TOGGLE_SETTING',
  UPDATE_SETTINGS = 'UPDATE_SETTINGS',
  
  // Design
  SET_PATTERN = 'SET_PATTERN',
  SET_CARD_THEME = 'SET_CARD_THEME',
  SET_BACKGROUND = 'SET_BACKGROUND',
  
  // Indicators
  SET_INDICATOR = 'SET_INDICATOR',
  SET_HINTS_COUNT = 'SET_HINTS_COUNT',
  
  // Buttons
  SHARE_BUTTON_CLICKED = 'SHARE_BUTTON_CLICKED',
  TUTORIAL_BUTTON_CLICKED = 'TUTORIAL_BUTTON_CLICKED',
  MAGIC_WAND_CLICKED = 'MAGIC_WAND_CLICKED',
  AUTOCOMPLETE_CLICKED = 'AUTOCOMPLETE_CLICKED'
}

// Universal action interface
export interface UpdateUIStateAction {
  type: UIActionTypes.UPDATE_UI_STATE;
  payload: Partial<UIState>;
}

// Action Interfaces
export interface UpdateGameStatsAction {
  type: UIActionTypes.UPDATE_GAME_STATS;
  payload: Partial<GameStats>;
}

export interface SetDifficultyAction {
  type: UIActionTypes.SET_DIFFICULTY;
  payload: DifficultyLevel;
}

export interface IncrementMovesAction {
  type: UIActionTypes.INCREMENT_MOVES;
}

export interface UpdateTimeAction {
  type: UIActionTypes.UPDATE_TIME;
  payload: number;
}

export interface ResetGameStatsAction {
  type: UIActionTypes.RESET_GAME_STATS;
}

export interface OpenDesignModalAction {
  type: UIActionTypes.OPEN_DESIGN_MODAL;
  payload?: DesignTab;
}

export interface CloseDesignModalAction {
  type: UIActionTypes.CLOSE_DESIGN_MODAL;
}

export interface SetDesignTabAction {
  type: UIActionTypes.SET_DESIGN_TAB;
  payload: DesignTab;
}

export interface OpenSettingsModalAction {
  type: UIActionTypes.OPEN_SETTINGS_MODAL;
}

export interface CloseSettingsModalAction {
  type: UIActionTypes.CLOSE_SETTINGS_MODAL;
}

export interface CloseAllPanelsAction {
  type: UIActionTypes.CLOSE_ALL_PANELS;
}

export interface ToggleSettingAction {
  type: UIActionTypes.TOGGLE_SETTING;
  payload: string;
}

export interface UpdateSettingsAction {
  type: UIActionTypes.UPDATE_SETTINGS;
  payload: Checkboxes[];
}

export interface SetPatternAction {
  type: UIActionTypes.SET_PATTERN;
  payload: string;
}

export interface SetCardThemeAction {
  type: UIActionTypes.SET_CARD_THEME;
  payload: string;
}

export interface SetBackgroundAction {
  type: UIActionTypes.SET_BACKGROUND;
  payload: string;
}

export interface SetIndicatorAction {
  type: UIActionTypes.SET_INDICATOR;
  payload: { key: keyof IndicatorsState; value: boolean | number };
}

export interface SetHintsCountAction {
  type: UIActionTypes.SET_HINTS_COUNT;
  payload: number;
}

export interface ShareButtonClickedAction {
  type: UIActionTypes.SHARE_BUTTON_CLICKED;
}

export interface TutorialButtonClickedAction {
  type: UIActionTypes.TUTORIAL_BUTTON_CLICKED;
}

export interface MagicWandClickedAction {
  type: UIActionTypes.MAGIC_WAND_CLICKED;
}

export interface AutocompleteClickedAction {
  type: UIActionTypes.AUTOCOMPLETE_CLICKED;
}

export type UIAction = 
  | UpdateUIStateAction
  | UpdateGameStatsAction
  | SetDifficultyAction
  | IncrementMovesAction
  | UpdateTimeAction
  | ResetGameStatsAction
  | OpenDesignModalAction
  | CloseDesignModalAction
  | SetDesignTabAction
  | OpenSettingsModalAction
  | CloseSettingsModalAction
  | CloseAllPanelsAction
  | ToggleSettingAction
  | UpdateSettingsAction
  | SetPatternAction
  | SetCardThemeAction
  | SetBackgroundAction
  | SetIndicatorAction
  | SetHintsCountAction
  | ShareButtonClickedAction
  | TutorialButtonClickedAction
  | MagicWandClickedAction
  | AutocompleteClickedAction; 